/* oxlint-disable no-console -- orphan purge diagnostics */
import {
  LOCAL_MEDIA_IDB_STORE,
  openSharedLocalIdb,
} from '@core/infrastructure/driven/indexedDb/sharedLocalIdb.ts';
import type { GetSqlJsDatabase } from '@src/modules/platform/local-sqlite/infrastructure/sqlJsRowHelpers.ts';
import { queryReferencedMediaIds } from '@src/modules/system/infrastructure/driven/localStorageDiagnostics.ts';

const MEDIA_READY_PROBE_KEY = '__esan_media_ready_probe__';

async function listAllMediaBlobIds(): Promise<string[]> {
  const idb = await openSharedLocalIdb();
  return new Promise((resolve, reject) => {
    const ids: string[] = [];
    const tx = idb.transaction(LOCAL_MEDIA_IDB_STORE, 'readonly');
    const store = tx.objectStore(LOCAL_MEDIA_IDB_STORE);
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      const key = String(cursor.key);
      if (key !== MEDIA_READY_PROBE_KEY) ids.push(key);
      cursor.continue();
    };
    req.onerror = () => reject(req.error ?? new Error('indexedDB cursor failed'));
    tx.oncomplete = () => resolve(ids);
    tx.onerror = () => reject(tx.error ?? new Error('indexedDB transaction failed'));
  });
}

export async function listOrphanMediaBlobIds(getDb: GetSqlJsDatabase): Promise<string[]> {
  const { signatureIds, imageIds } = queryReferencedMediaIds(getDb);
  const referenced = new Set<string>([...signatureIds, ...imageIds]);
  const all = await listAllMediaBlobIds();
  return all.filter((id) => !referenced.has(id));
}

export async function countOrphanMediaBlobs(getDb: GetSqlJsDatabase): Promise<number> {
  return (await listOrphanMediaBlobIds(getDb)).length;
}

/** Deletes media blobs not referenced by any local entrega/reposición row. */
export async function purgeOrphanMediaBlobs(getDb: GetSqlJsDatabase): Promise<number> {
  const orphans = await listOrphanMediaBlobIds(getDb);
  if (orphans.length === 0) return 0;
  const idb = await openSharedLocalIdb();
  await new Promise<void>((resolve, reject) => {
    const tx = idb.transaction(LOCAL_MEDIA_IDB_STORE, 'readwrite');
    const store = tx.objectStore(LOCAL_MEDIA_IDB_STORE);
    for (const id of orphans) store.delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('indexedDB delete orphans failed'));
  });
  console.info(`[local-media] purged ${orphans.length} orphan blob(s)`);
  return orphans.length;
}
